import { DropShadowFilter as PixiDropShadowFilter } from 'pixi-filters';
import { Config } from '../../video-data/config';
import { ColorAttribute } from '../attributes';
import { assertMinimum, assertNumber, assertNumberBetween } from '../../validators';
import type { BaseSettings } from '../TimeAwareBase';
import { Filter } from './Filter';
import { comparer, observable, reaction } from '../reactivity';

/**
 * A drop shadow cast by the asset's visible shape. It is drawn outside the asset's masks, so a
 * masked or rounded media still casts a shadow beyond its edge. Distance and blur are in
 * reference-resolution pixels.
 */
export class DropShadowFilter extends Filter {
    static readonly NEUTRAL_ALPHA = 0;

    override _classType = Config.DROP_SHADOW_FILTER;
    override pixiFilter: PixiDropShadowFilter;
    @observable accessor #distance = 10;
    @observable accessor #angle = Math.PI / 4;
    @observable accessor #blur = 4;
    @observable accessor #alpha = 0.5;
    @ColorAttribute()
    @observable
    accessor color = 0x000000;

    constructor(settings: BaseSettings) {
        super(settings);
        this.pixiFilter = new PixiDropShadowFilter({ quality: 5 });

        this.addReaction(
            reaction(
                () => ({
                    distance: this.#distance,
                    angle: this.#angle,
                    blur: this.#blur,
                    alpha: this.#alpha,
                    color: this.color,
                }),
                ({ distance, angle, blur, alpha, color }) => {
                    this.pixiFilter.offset = {
                        x: Math.cos(angle) * distance,
                        y: Math.sin(angle) * distance,
                    };
                    this.pixiFilter.blur = blur;
                    this.pixiFilter.alpha = alpha;
                    this.pixiFilter.color = color;
                },
                {
                    equals: comparer.shallow,
                    fireImmediately: true,
                    name: 'DropShadowFilter.uniforms',
                },
            ),
        );
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['distance', 'angle', 'blur', 'alpha', 'color']);
    }

    get distance(): number {
        return this.#distance;
    }

    set distance(value: number) {
        if (value === this.#distance) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#distance = value;
        this.requiresUpdate = true;
    }

    /**
     * The direction the shadow is cast in, in radians clockwise from the positive x axis.
     */
    get angle(): number {
        return this.#angle;
    }

    set angle(value: number) {
        if (value === this.#angle) {
            return;
        }
        assertNumber(value);
        this.#angle = value;
        this.requiresUpdate = true;
    }

    get blur(): number {
        return this.#blur;
    }

    set blur(value: number) {
        if (value === this.#blur) {
            return;
        }
        assertNumber(value);
        assertMinimum(value, 0);
        this.#blur = value;
        this.requiresUpdate = true;
    }

    get alpha(): number {
        return this.#alpha;
    }

    set alpha(value: number) {
        if (value === this.#alpha) {
            return;
        }
        assertNumberBetween(value, 0, 1);
        this.#alpha = value;
        this.requiresUpdate = true;
    }

    override isNeutral(): boolean {
        return this.alpha === DropShadowFilter.NEUTRAL_ALPHA;
    }
}
